import type { Application } from '../types';
import { FunnelStageBadge } from './FunnelStageBadge';
import { ScoreRing } from './ui';

interface FunnelCardProps {
  application: Application;
  jobId: number;
  onOpen: (applicationId: number) => void;
}

function getInitials(first?: string, last?: string): string {
  return `${(first?.[0] ?? '').toUpperCase()}${(last?.[0] ?? '').toUpperCase()}`;
}

function avatarColor(name: string): string {
  const colors = [
    'bg-sky-500', 'bg-emerald-500', 'bg-amber-500',
    'bg-purple-500', 'bg-rose-500', 'bg-teal-500',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return colors[Math.abs(hash) % colors.length];
}

function formatActivity(lastActivityAt: string | null): string {
  if (!lastActivityAt) return '';
  const diff = Date.now() - new Date(lastActivityAt).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return 'Active today';
  if (days === 1) return 'Active 1d ago';
  return `Active ${days}d ago`;
}

export function FunnelCard({ application, jobId: _jobId, onOpen }: FunnelCardProps) {
  const { candidateFirstName, candidateLastName, srStage, score, lastActivityAt } = application;
  const fullName = `${candidateFirstName ?? ''} ${candidateLastName ?? ''}`.trim();
  const initials = getInitials(candidateFirstName, candidateLastName);
  const color = avatarColor(fullName);

  function handleOpen() {
    onOpen(application.id);
  }

  return (
    <div
      data-testid="funnel-card"
      onClick={handleOpen}
      className="bg-white dark:bg-gray-800 border border-stone-200 dark:border-gray-700 rounded-lg p-2.5 cursor-pointer hover:border-sky-300 dark:hover:border-sky-700 hover:shadow-[0_0_0_3px_theme(colors.sky.50)] dark:hover:shadow-none transition-colors"
    >
      {/* Row 1: avatar + name + score */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-8 h-8 rounded-lg ${color} text-white text-xs font-bold flex items-center justify-center flex-shrink-0`}>
          {initials}
        </div>
        <span className="flex-1 min-w-0 text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate">
          {fullName || 'Unknown'}
        </span>
        {score != null && (
          <ScoreRing score={score} size="sm" />
        )}
      </div>

      {/* Row 2: activity */}
      {lastActivityAt && (
        <div className="text-[11px] text-stone-400 dark:text-gray-500 mb-2">
          {formatActivity(lastActivityAt)}
        </div>
      )}

      {/* Row 3: stage badge + view button */}
      <div className="flex items-center justify-between">
        {srStage && <FunnelStageBadge stage={srStage} />}
        <button
          onClick={(e) => { e.stopPropagation(); handleOpen(); }}
          className="text-[11px] font-medium text-sky-600 dark:text-sky-400 hover:text-sky-500 dark:hover:text-sky-300 border-none bg-transparent p-0 cursor-pointer"
        >
          View →
        </button>
      </div>
    </div>
  );
}
